import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import { SectionLayout } from '@/components/section-layout';
import { useOpenEntity } from '@/hooks/useOpenEntity';
import { NavigationService } from '@/services/navigationService';
import { getSession as getCachedSession } from '@/lib/auth-session';
import { fetchEntity, fetchEntityList, fetchEntityExport } from '@/lib/entity-api';
import { useEntityList } from '@/hooks/useEntityList';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { exportEntityCsv } from '@/lib/csv';
import { fetchChecklistAttachments } from './store';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { getApiUrl } from '@/lib/api-config';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Copy, Trash2, Download, Upload } from 'lucide-react';
import { ChecklistForm } from '@/features/project-management/checklists/ChecklistForm';
import { trackChecklistCreated, trackChecklistUpdated, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { failureReason } from '@/lib/permission-errors';

// Types



type FileAttachment = GenericAttachment;

interface Checklist {
  id: string;
  checklist_id?: string | null;
  title: string;
  description: string;
  status: string;
  priority: string;
  due_date: string;
  due_time: string;
  project_id: string;
  assignee_id: string;
  created_by: string;
  company_id: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  reminder_times: string | null;
  assignee?: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
  project?: {
    id: string;
    name: string;
  };
  attachments: { count: number }[];
  comments: { count: number }[];
  attachment_count: number;
  checklist_items?: Array<{
    id: string;
    text: string;
    completed: boolean;
    item_order: number;
  }>;
}

export function ChecklistsPage() {
  const { selectedProject } = useProject();
  const { companyId, user } = useUser();
  const { shouldOpen, entityId } = useOpenEntity('checklist');
  // The list: ONE server-driven query (project + search + sort + page) -- search, sort and paging run in the database
  // over every checklist the user may see; the counters come with each row (hooks/useEntityList.ts).
  const list = useEntityList<any, Checklist>({
    entityType: 'checklist',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'checklist_id', direction: 'asc', sortKey: 'checklist_id' },
    mapRow: (row) => ({
      ...row,
      attachments: [{ count: row.attachment_count || 0 }],
      comments: [{ count: row.comment_count || 0 }],
    }),
  });
  const checklists = list.items;
  const { projects, users } = useCompanyLookups(!!companyId);
  // Latest values for the effect that opens a record from navigation (read when it runs, never a reason to re-run it).
  const checklistsRef = useRef(checklists);
  checklistsRef.current = checklists;
  const handleRowClickRef = useRef<(row: Checklist) => void>(() => {});
  const [isNewChecklistOpen, setIsNewChecklistOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(null);
  const [originalEditingChecklist, setOriginalEditingChecklist] = useState<Checklist | null>(null);
  // The cached auth session the form receives.
  const session = getCachedSession();
  const [checklistAttachments, setChecklistAttachments] = useState<Record<string, FileAttachment[]>>({});
  const [isLoadingChecklistDetails, setIsLoadingChecklistDetails] = useState(false);

  // Check if we should open a specific checklist from navigation
  useEffect(() => {
    if (!shouldOpen || !entityId || !list.hasLoaded) return;
    // Clear the state to prevent reopening on refresh
    NavigationService.clearOpenState();
    // The record may not be on the page that is showing: ask the API for it.
    const loaded = checklistsRef.current.find((c) => c.id === entityId);
    (loaded ? Promise.resolve(loaded) : fetchEntity<any>('checklist', entityId, { includeCounts: true }))
      .then((row) => handleRowClickRef.current(row))
      .catch((error) => console.error('Error opening checklist:', error));
  }, [shouldOpen, entityId, list.hasLoaded]);

  const handleCreateChecklist = async (formData: any) => {
    try {
      if (!user || !companyId) return;

      const { checklist_items, attachmentsFiles, ...checklistData } = formData;

      // Use generic API for checklist creation
      const response = await fetch(getApiUrl('api/entities/checklist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          company_id: companyId,
          ...checklistData,
          created_by: user.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checklist');
      }

      const result = await response.json();
      const data = result.data;

      if (checklist_items && Array.isArray(checklist_items)) {
        if (checklist_items.length > 0) {
          const itemsToInsert = checklist_items.map((item: any, index: number) => ({
            checklist_id: data.id,
            text: item.text,
            completed: item.completed || false,
            item_order: index + 1,
            company_id: companyId
          }));

          // Use generic API for checklist_item creation (bulk)
          const insertResponse = await fetch(getApiUrl('api/entities/checklist_item/bulk'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId,
              items: itemsToInsert
            })
          });

          if (!insertResponse.ok) {
            const errorData = await insertResponse.json();
            throw new Error(errorData.error || 'Failed to create checklist items');
          }
        }
      }

      if (attachmentsFiles && attachmentsFiles.length > 0) {
        try {
          const { uploadChecklistAttachments } = await import('./store');
          await uploadChecklistAttachments(data.id, attachmentsFiles, companyId, user.id);
        } catch (error) {
          console.error('Error uploading attachments:', error);
        }
      }

      list.reload();
      setIsNewChecklistOpen(false);

      // Track successful checklist creation with PostHog
      trackChecklistCreated({
        checklist_id: data.id,
        title: checklistData.title,
        status: checklistData.status,
        priority: checklistData.priority,
        project_id: checklistData.project_id,
        assignee_id: checklistData.assignee_id,
        due_date: checklistData.due_date,
        item_count: checklist_items?.length || 0,
        attachment_count: attachmentsFiles?.length || 0,
        company_id: companyId,
        created_by: user.id
      });

      // Track successful form submission
      trackFormSubmission({
        form_type: 'checklist_creation',
        success: true
      });
    } catch (error: any) {
      console.error('Error creating checklist:', error);
      
      // Track failed checklist creation with PostHog
      trackFormSubmission({
        form_type: 'checklist_creation',
        success: false,
        error_message: error.message || 'Failed to create checklist'
      });

      setError(error.message || 'Failed to create checklist');
    }
  };

  const handleRowClick = async (checklist: Checklist) => {
    setEditingChecklist(checklist);
    setOriginalEditingChecklist(checklist);
    setIsEditModalOpen(true);
    setIsLoadingChecklistDetails(true);

    try {
      // The form loads its own comments (useEntityComments); here: the files and the items of THIS checklist.
      const [attachmentsResult, itemRows] = await Promise.all([
        fetchChecklistAttachments(checklist.id),
        fetchEntityList<any>('checklist_item', { filters: { checklist_id: checklist.id } }),
      ]);

      setChecklistAttachments(prev => ({
        ...prev,
        [checklist.id]: attachmentsResult
      }));

      const checklistItems = [...itemRows]
        .sort((a, b) => (a.item_order ?? 0) - (b.item_order ?? 0))
        .map(item => ({
          id: item.id,
          text: item.text,
          completed: item.completed,
          item_order: item.item_order
        }));

      setEditingChecklist({ ...checklist, checklist_items: checklistItems });
      // Don't update originalEditingChecklist - keep it as the original task data
    } catch (error) {
      console.error('Error fetching checklist details:', error);
    } finally {
      setIsLoadingChecklistDetails(false);
    }
  };
  handleRowClickRef.current = handleRowClick;

  const handleSaveChanges = async (formData: any) => {
    try {
      if (!editingChecklist) return;

      const { checklist_items, attachmentsFiles, ...updateData } = formData;

      // Use generic API for checklist update
      const response = await fetch(getApiUrl(`api/entities/checklist/${editingChecklist.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          company_id: companyId,
          title: updateData.title,
          description: updateData.description,
          project_id: updateData.project_id,
          assignee_id: updateData.assignee_id || null,
          status: updateData.status,
          priority: updateData.priority,
          due_date: updateData.due_date,
          due_time: updateData.due_time,
          reminder_times: updateData.reminder_times,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update checklist');
      }

      const result = await response.json();
      const data = result.data;

      if (checklist_items && Array.isArray(checklist_items)) {
        // Use generic API for checklist_item deletion (bulk)
        const deleteResponse = await fetch(getApiUrl(`api/entities/checklist_item/bulk/${editingChecklist.id}`), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId,
            checklist_id: editingChecklist.id
          })
        });

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json();
          throw new Error(errorData.error || 'Failed to delete checklist items');
        }

        if (checklist_items.length > 0) {
          const itemsToInsert = checklist_items.map((item: any, index: number) => ({
            checklist_id: editingChecklist.id,
            text: item.text,
            completed: item.completed || false,
            item_order: index + 1,
            company_id: companyId
          }));

          // Use generic API for checklist_item creation (bulk)
          const insertResponse = await fetch(getApiUrl('api/entities/checklist_item/bulk'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId,
              items: itemsToInsert
            })
          });

          if (!insertResponse.ok) {
            const errorData = await insertResponse.json();
            throw new Error(errorData.error || 'Failed to create checklist items');
          }
        }
      }

      list.setItems((prev) => prev.map((c) => (c.id === editingChecklist.id ? { ...c, ...data } : c)));
      
      // Track successful checklist update with PostHog
      trackChecklistUpdated({
        checklist_id: editingChecklist.id,
        title: updateData.title,
        status: updateData.status,
        priority: updateData.priority,
        project_id: updateData.project_id,
        assignee_id: updateData.assignee_id,
        due_date: updateData.due_date,
        item_count: checklist_items?.length || 0,
        attachment_count: attachmentsFiles?.length || 0,
        company_id: companyId,
        updated_by: user.id
      });

      // Track successful form submission
      trackFormSubmission({
        form_type: 'checklist_update',
        success: true
      });
      
      setIsEditModalOpen(false);
      setEditingChecklist(null);
      setOriginalEditingChecklist(null);
    } catch (error: any) {
      console.error('Error updating checklist:', error);
      
      // Track failed checklist update with PostHog
      trackFormSubmission({
        form_type: 'checklist_update',
        success: false,
        error_message: error.message || 'Failed to update checklist'
      });

      setError(error.message || 'Failed to update checklist');
    }
  };

  // 404 fix (2026-09-27): this used to ALSO call
  // `DELETE /api/entities/checklist_attachment/:id` itself -- but
  // ChecklistForm's own handleDeleteFile (useAttachmentStaging's deleteFn,
  // see checklists/store.ts's deleteChecklistAttachment) already performs
  // the real deletion BEFORE calling this callback. That made every
  // delete hit the endpoint twice; the second call always 404'd because
  // the row was already soft-deleted by the first. This callback's only
  // job is to sync this page's own cached attachment list, same as
  // Specifications' page.tsx already did correctly -- never call the
  // network again here.
  const handleDeleteAttachment = (checklistId: string, attachmentId: string) => {
    setChecklistAttachments(prev => ({
      ...prev,
      [checklistId]: prev[checklistId]?.filter(att => att.id !== attachmentId) || []
    }));
  };

  const handleCloseEditModal = () => {
    // GenericForm handles unsaved changes warnings internally
    setIsEditModalOpen(false);
    setEditingChecklist(null);
    setOriginalEditingChecklist(null);
  };

  const getProjectName = (projectId: string) => {
    if (!projects || !Array.isArray(projects)) return projectId;
    const project = projects.find(p => p.id === projectId);
    return project?.name || projectId;
  };

  const getAssigneeName = (assigneeId: string | null) => {
    if (!assigneeId) return 'Unassigned';
    if (!users || !Array.isArray(users)) return assigneeId;
    const user = users.find(u => u.id === assigneeId);
    return user?.full_name || assigneeId;
  };

  // Map priority to GenericTag priority types (urgent -> high)
  const mapPriorityToTag = (priority: string): 'low' | 'medium' | 'high' => {
    switch (priority.toLowerCase()) {
      case 'low':
        return 'low';
      case 'medium':
        return 'medium';
      case 'high':
      case 'urgent':
        return 'high'; // Map urgent to high
      default:
        return 'medium';
    }
  };

  // Map checklist statuses to GenericTag status types
  const mapChecklistStatusToTag = (status: string): 'draft' | 'sent' | 'approved' | 'rejected' | 'pending' | 'in_progress' | 'completed' => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'draft';
      case 'sent':
        return 'sent';
      case 'pending':
        return 'pending';
      case 'in_progress':
        return 'in_progress';
      case 'completed':
        return 'completed';
      case 'cancelled':
        return 'rejected';
      default:
        return 'draft';
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    // Parse date string directly to avoid timezone conversion, same as form
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString();
  };

  const formatChecklistId = (checklist: Checklist): string => {
    const checklistId = (checklist as any).checklist_id;
    if (typeof checklistId === 'string') {
      return checklistId;
    } else if (typeof checklistId === 'number') {
      return `CH${checklistId.toString().padStart(6, '0')}`;
    }
    return '-';
  };

  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one checklist to copy.');
      return;
    }

    if (!user || !companyId) return;

    try {
      let successCount = 0;
      let failureCount = 0;

      for (const checklistId of selectedRows) {
        try {
          // Fetch the checklist with its items
          // (the checklist's own columns, through the export read; then its items)
          const [checklistData] = await fetchEntityExport<any>('checklist', {}, { ids: [checklistId] });

          if (!checklistData) {
            failureCount++;
            continue;
          }

          // Fetch checklist items
          let itemsData: any[] = [];
          try {
            itemsData = (await fetchEntityList<any>('checklist_item', { filters: { checklist_id: checklistId } }))
              .sort((a, b) => (a.item_order ?? 0) - (b.item_order ?? 0));
          } catch (itemsError) {
            console.error('Error fetching checklist items:', itemsError);
          }

          // Prepare new checklist data (exclude id, checklist_id, timestamps)
          const { id, checklist_id, created_at, updated_at, completed_at, ...checklistPayload } = checklistData;

          // Create new checklist via generic API
          const response = await fetch(getApiUrl('api/entities/checklist'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId,
              ...checklistPayload,
              status: 'pending', // Set status to pending when copying
              created_by: user.id,
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create checklist');
          }

          const result = await response.json();
          const newChecklistId = result.data.id;

          // Copy checklist items (if any)
          if (itemsData && itemsData.length > 0) {
            const itemsToInsert = itemsData.map((item: any, index: number) => ({
              checklist_id: newChecklistId,
              text: item.text,
              completed: false, // Reset completed status
              item_order: index + 1,
              company_id: companyId
            }));

            // Use generic API for checklist_item creation (bulk)
            const insertResponse = await fetch(getApiUrl('api/entities/checklist_item/bulk'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: user.id,
                company_id: companyId,
                items: itemsToInsert
              })
            });

            if (!insertResponse.ok) {
              const errorData = await insertResponse.json();
              throw new Error(errorData.error || 'Failed to create checklist items');
            }
          }

          successCount++;
        } catch (copyError) {
          console.error('Error copying checklist:', copyError);
          failureCount++;
        }
      }

      if (successCount > 0) {
        list.reload();
        setSelectedRows([]);
      }

      if (failureCount > 0) {
        alert(`Copied ${successCount} checklist(s). ${failureCount} failed.`);
      } else if (successCount > 0) {
        alert(`Copied ${successCount} checklist(s) successfully.`);
      }
    } catch (error) {
      console.error('Error copying checklists:', error);
      alert('Failed to copy checklists. Please try again.');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one checklist to delete.');
      return;
    }

    if (!window.confirm(`Delete ${selectedRows.length} checklist(s)? This action cannot be undone.`)) {
      return;
    }

    if (!user || !companyId) return;

    try {
      let successCount = 0;
      let failureCount = 0;
      // Why each failed delete failed (generic for a permission refusal, never an internal server detail); the technical error stays in the console
      const reasons = new Set<string>();

      for (const checklistId of selectedRows) {
        try {
          const response = await fetch(getApiUrl(`api/entities/checklist/${checklistId}`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId
            })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            console.error('Error deleting checklist:', response.status, errorData);
            reasons.add(failureReason(response.status, errorData));
            failureCount++;
            continue;
          }

          successCount++;
        } catch (deleteError) {
          console.error('Error deleting checklist:', deleteError);
          reasons.add(failureReason(0, null));
          failureCount++;
        }
      }

      if (successCount > 0) {
        list.reload();
        setSelectedRows([]);
      }

      if (failureCount > 0) {
        // partial results are kept: what was deleted, what was not, and why
        alert(`Deleted ${successCount} checklist(s). ${failureCount} failed: ${Array.from(reasons).join(' ')}`);
      } else if (successCount > 0) {
        alert(`Deleted ${successCount} checklist(s) successfully.`);
      }
    } catch (error) {
      console.error('Error deleting checklists:', error);
      alert('Failed to delete checklists. Please try again.');
    }
  };

  // Export to CSV: the rows selected in the table, or -- with nothing selected -- EVERY checklist of the current
  // project + search, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'checklists', 'checklists');

  // Define table columns
  const columns: GenericTableColumn<Checklist>[] = [
    {
      key: 'checklist_id',
      header: 'Checklist ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'checklist_id',
      render: (row) => <span className="font-medium">{formatChecklistId(row)}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      width: '12.5rem', // 200px = 12.5rem
      sortable: true,
      sortKey: 'title',
      render: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '9.375rem', // 150px = 9.375rem
      sortable: true,
      sortKey: 'project',
      render: (row) => getProjectName(row.project_id),
    },
    {
      key: 'assignee_id',
      header: 'Assignee',
      width: '9.375rem', // 150px = 9.375rem
      sortable: true,
      sortKey: 'assignee',
      render: (row) => getAssigneeName(row.assignee_id),
    },
    {
      key: 'status',
      header: 'Status',
      width: '7.0rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapChecklistStatusToTag(row.status)}
        />
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '6rem',
      sortable: true,
      sortKey: 'priority',
      render: (row) => (
        <GenericTag 
          type="priority" 
          value={mapPriorityToTag(row.priority)}
        />
      ),
    },
    {
      key: 'due_date',
      header: 'Due Date',
      width: '6.5rem',
      sortable: true,
      sortKey: 'due_date',
      render: (row) => formatDate(row.due_date),
    },
    {
      key: 'attachments',
      header: 'Attachments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.attachments?.[0]?.count || 0,
    },
    {
      key: 'comments',
      header: 'Comments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.comments?.[0]?.count || 0,
    },
  ];

  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => console.log('Import clicked'),
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  if (!list.hasLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <SectionLayout title="Checklists">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={() => {
                trackButtonClick('new_checklist_button', { context: 'project_management' });
                setIsNewChecklistOpen(true);
              }}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleRowClick}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
        />

      {isNewChecklistOpen && (
          <ChecklistForm
            mode="create"
            projects={projects}
            users={users}
            onSubmit={handleCreateChecklist}
            onCancel={() => setIsNewChecklistOpen(false)}
          />
      )}

      {isEditModalOpen && (
        isLoadingChecklistDetails ? createPortal(
          <div className="generic-form-overlay">
            <div className="generic-form-container" style={{ padding: '2rem' }}>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-4">
                  <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                  <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                </div>
                <p className="text-sm text-gray-500 font-medium">Loading...</p>
              </div>
            </div>
          </div>,
          document.body
          ) : editingChecklist && (
            <ChecklistForm
              mode="edit"
              initialData={editingChecklist}
              projects={projects}
              users={users}
              onSubmit={handleSaveChanges}
              onCancel={handleCloseEditModal}
              onDeleteAttachment={(id) => handleDeleteAttachment(editingChecklist.id, id)}
              attachments={checklistAttachments[editingChecklist.id] || []}
            />
        )
          )}
      </div>
    </SectionLayout>
  );
}